import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Folder,
  HardDrive,
  Infinity as InfinitoIcono,
  Minus,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import type { NodoArbol, UsoDeCarpeta } from '@doc/shared';
import { ETIQUETA_ESTADO_CUOTA } from '@doc/shared';
import { api } from '../../lib/api.js';
import { tamano } from '../../lib/formato.js';
import { Cabecera } from '../../layout/Marco.js';
import { BarraCuota } from '../../ui/BarraCuota.js';
import { Confirmar, Modal } from '../../ui/Modal.js';
import { useAvisos } from '../../ui/avisos.js';
import { claves } from '../explorador/consultas.js';

interface Respuesta {
  cuotas: UsoDeCarpeta[];
  organizacion: {
    nombre: string;
    usadoBytes: number;
    papeleraBytes: number;
    archivos: number;
    versiones: number;
    asignadoBytes: number;
    enAlerta: number;
  };
}

export function CuotasPage() {
  const cliente = useQueryClient();
  const { avisar, avisarError } = useAvisos();
  const [asignando, setAsignando] = useState<UsoDeCarpeta | 'nueva' | null>(null);
  const [quitando, setQuitando] = useState<UsoDeCarpeta | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['cuotas'], queryFn: () => api<Respuesta>('/cuotas') });

  const fijar = useMutation({
    mutationFn: (v: { nodoId: string; cuotaGB: number | null }) =>
      api<UsoDeCarpeta & { yaExcedida: boolean }>(`/nodos/${v.nodoId}/cuota`, {
        metodo: 'PUT',
        cuerpo: { cuotaGB: v.cuotaGB },
      }),
    onSuccess: (resultado, variables) => {
      void cliente.invalidateQueries({ queryKey: ['cuotas'] });
      void cliente.invalidateQueries({ queryKey: ['cuota'] });
      void cliente.invalidateQueries({ queryKey: claves.arbol });
      if (variables.cuotaGB === null) avisar(`"${resultado.nombre}" queda sin límite de almacenamiento`);
      else if (resultado.yaExcedida)
        avisar(`Cuota fijada, pero "${resultado.nombre}" ya la supera: no se podrá subir nada hasta liberar espacio`, 'aviso');
      else avisar(`Cuota de "${resultado.nombre}" actualizada`);
      setAsignando(null);
      setQuitando(null);
    },
    onError: avisarError,
  });

  const enAlerta = useMemo(
    () => (data?.cuotas ?? []).filter((c) => c.estado === 'ALERTA' || c.estado === 'LLENA'),
    [data],
  );

  return (
    <>
      <Cabecera
        titulo="Cuotas de almacenamiento"
        subtitulo="Cuánto espacio tiene asignado cada carpeta y cuánto lleva usado"
        acciones={
          <button className="btn btn--primario" onClick={() => setAsignando('nueva')}>
            <Plus size={14} /> Asignar cuota
          </button>
        }
      />

      <div className="pagina">
        {isLoading && <div className="cargando">Cargando…</div>}

        {data && (
          <>
            {enAlerta.length > 0 && (
              <div className="alerta-cuotas" role="alert">
                <AlertTriangle size={18} />
                <div>
                  <strong>
                    {enAlerta.length === 1
                      ? '1 carpeta necesita atención'
                      : `${enAlerta.length} carpetas necesitan atención`}
                  </strong>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {enAlerta
                      .map((c) => `${c.nombre} (${c.porcentaje}%)`)
                      .slice(0, 4)
                      .join(' · ')}
                    {enAlerta.length > 4 && ` y ${enAlerta.length - 4} más`}
                  </div>
                </div>
              </div>
            )}

            <div className="tarjetas-cuota">
              <Resumen
                icono={<HardDrive size={16} />}
                titulo="Usado en la organización"
                valor={tamano(data.organizacion.usadoBytes)}
                detalle={`${data.organizacion.archivos} archivos · ${data.organizacion.versiones} versiones`}
              />
              <Resumen
                icono={<Folder size={16} />}
                titulo="Espacio asignado"
                valor={data.organizacion.asignadoBytes > 0 ? tamano(data.organizacion.asignadoBytes) : '—'}
                detalle={`${data.cuotas.length} carpeta(s) con cuota`}
              />
              <Resumen
                icono={<Trash2 size={16} />}
                titulo="Retenido en la papelera"
                valor={tamano(data.organizacion.papeleraBytes)}
                detalle="Sigue ocupando cuota hasta purgarlo"
              />
              <Resumen
                icono={<AlertTriangle size={16} />}
                titulo="Sobre el 80%"
                valor={String(data.organizacion.enAlerta)}
                detalle={data.organizacion.enAlerta === 0 ? 'Todo con holgura' : 'Requieren ampliación o limpieza'}
                alerta={data.organizacion.enAlerta > 0}
              />
            </div>

            {data.cuotas.length === 0 ? (
              <div className="vacio">
                <div className="icono">
                  <InfinitoIcono size={36} />
                </div>
                <p>Ninguna carpeta tiene cuota: hoy todas pueden crecer sin límite.</p>
                <button className="btn btn--primario" onClick={() => setAsignando('nueva')}>
                  <Plus size={14} /> Asignar la primera
                </button>
              </div>
            ) : (
              <table className="tabla tabla-cuotas">
                <thead>
                  <tr>
                    <th>Carpeta</th>
                    <th style={{ width: 260 }}>Uso</th>
                    <th style={{ width: 110 }}>Cuota</th>
                    <th style={{ width: 110 }}>Libre</th>
                    <th style={{ width: 120 }}>Estado</th>
                    <th style={{ width: 130 }} />
                  </tr>
                </thead>
                <tbody>
                  {data.cuotas.map((c) => (
                    <tr key={c.nodoId}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Folder size={15} style={{ color: 'var(--green-light)', flex: 'none' }} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 500 }}>{c.nombre}</div>
                            <div className="dim" style={{ fontSize: 11 }}>
                              {c.ruta}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <BarraCuota
                          usadoBytes={c.usadoBytes}
                          cuotaBytes={c.cuotaBytes}
                          estado={c.estado}
                          porcentaje={c.porcentaje}
                        />
                      </td>
                      <td>{c.cuotaBytes === null ? '—' : tamano(c.cuotaBytes)}</td>
                      <td className={c.estado === 'LLENA' ? 'aviso-rojo' : ''}>
                        {c.libreBytes === null ? '—' : tamano(c.libreBytes)}
                      </td>
                      <td>
                        <span
                          className={`badge${c.estado === 'OK' ? ' badge--verde' : c.estado === 'ALERTA' ? ' badge--ambar' : c.estado === 'LLENA' ? ' badge--rojo' : ''}`}
                        >
                          {ETIQUETA_ESTADO_CUOTA[c.estado]}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn--sm" onClick={() => setAsignando(c)}>
                          Cambiar
                        </button>{' '}
                        <button className="btn btn--sm btn--peligro" title="Quitar el límite" onClick={() => setQuitando(c)}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <p className="muted" style={{ fontSize: 11.5, marginTop: 16, lineHeight: 1.6, maxWidth: 720 }}>
              La cuota de una carpeta rige para todo lo que cuelga de ella, y se cuentan las versiones antiguas y lo
              que está en la papelera: ocupan disco igual. Si hay cuotas anidadas manda la más apretada. Al llegar al
              límite se bloquean las subidas, las copias y los movimientos hacia esa carpeta; leer y descargar sigue
              funcionando.
            </p>
          </>
        )}
      </div>

      {asignando && (
        <DialogoCuota
          objetivo={asignando === 'nueva' ? null : asignando}
          guardando={fijar.isPending}
          alGuardar={(nodoId, cuotaGB) => fijar.mutate({ nodoId, cuotaGB })}
          alCerrar={() => setAsignando(null)}
        />
      )}

      {quitando && (
        <Confirmar
          titulo={`Quitar la cuota de "${quitando.nombre}"`}
          etiquetaConfirmar="Quitar el límite"
          mensaje={
            <>
              La carpeta podrá volver a crecer sin límite. Hoy ocupa <strong>{tamano(quitando.usadoBytes)}</strong>.
            </>
          }
          alConfirmar={() => fijar.mutate({ nodoId: quitando.nodoId, cuotaGB: null })}
          alCerrar={() => setQuitando(null)}
        />
      )}
    </>
  );
}

function Resumen({
  icono,
  titulo,
  valor,
  detalle,
  alerta,
}: {
  icono: React.ReactNode;
  titulo: string;
  valor: string;
  detalle: string;
  alerta?: boolean;
}) {
  return (
    <div className={`tarjeta-cuota${alerta ? ' alerta' : ''}`}>
      <div className="tarjeta-cuota__titulo">
        {icono} {titulo}
      </div>
      <div className="tarjeta-cuota__valor">{valor}</div>
      <div className="tarjeta-cuota__detalle">{detalle}</div>
    </div>
  );
}

interface PropsDialogo {
  objetivo: UsoDeCarpeta | null;
  guardando: boolean;
  alGuardar(nodoId: string, cuotaGB: number | null): void;
  alCerrar(): void;
}

function DialogoCuota({ objetivo, guardando, alGuardar, alCerrar }: PropsDialogo) {
  const [carpeta, setCarpeta] = useState<{ id: string; nombre: string } | null>(
    objetivo ? { id: objetivo.nodoId, nombre: objetivo.nombre } : null,
  );
  // Valor + unidad, no "GB" a secas: una cuota de 500 MB expresada en GB es
  // 0,488 y una de 105 KB se redondeaba a 0, dejando el formulario inservible.
  const inicial = tamanoEditable(objetivo?.cuotaBytes ?? 10 * UNIDADES.GB);
  const [valor, setValor] = useState(inicial.valor);
  const [unidad, setUnidad] = useState<Unidad>(inicial.unidad);
  const [busqueda, setBusqueda] = useState('');

  const { data: arbol } = useQuery({
    queryKey: claves.arbol,
    queryFn: () => api<NodoArbol[]>('/nodos/arbol'),
    enabled: !objetivo,
  });

  const filtradas = (arbol ?? []).filter(
    (c) => c.permisos.leer && (!busqueda || c.nombre.toLowerCase().includes(busqueda.toLowerCase())),
  );

  const bytes = Math.round(valor * UNIDADES[unidad]);
  const ajustar = (delta: number) => setValor((v) => Math.max(0.001, +(v + delta).toFixed(3)));

  return (
    <Modal
      titulo={objetivo ? `Cuota de "${objetivo.nombre}"` : 'Asignar cuota a una carpeta'}
      icono={<HardDrive size={17} />}
      alCerrar={alCerrar}
      pie={
        <>
          <button className="btn" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="btn btn--primario"
            disabled={!carpeta || guardando || !(bytes > 0)}
            onClick={() => carpeta && alGuardar(carpeta.id, bytes / UNIDADES.GB)}
          >
            {guardando ? 'Guardando…' : 'Guardar cuota'}
          </button>
        </>
      }
    >
      {!objetivo && (
        <div className="campo">
          <label>Carpeta</label>
          {carpeta ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Folder size={15} style={{ color: 'var(--green-light)' }} />
              <strong>{carpeta.nombre}</strong>
              <button className="btn btn--sm btn--fantasma" onClick={() => setCarpeta(null)}>
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <div className="buscador">
                <Search size={14} />
                <input
                  className="input"
                  style={{ paddingLeft: 30 }}
                  placeholder="Buscar carpeta…"
                  autoFocus
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
              <div className="lista-carpetas">
                {filtradas.map((c) => (
                  <button key={c.id} className="selector-sujeto__item" onClick={() => setCarpeta({ id: c.id, nombre: c.nombre })}>
                    <Folder size={14} style={{ color: 'var(--green-light)' }} />
                    {c.nombre}
                  </button>
                ))}
                {filtradas.length === 0 && <div className="muted" style={{ padding: 10 }}>Sin coincidencias</div>}
              </div>
            </>
          )}
        </div>
      )}

      <div className="campo">
        <label>Espacio asignado</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn--icono" onClick={() => ajustar(-1)} title={`Quitar 1 ${unidad}`}>
            <Minus size={14} />
          </button>
          <input
            className="input"
            style={{ width: 120, textAlign: 'center' }}
            type="number"
            min={0.001}
            step={0.5}
            value={valor}
            onChange={(e) => setValor(Number(e.target.value))}
          />
          <button className="btn btn--icono" onClick={() => ajustar(1)} title={`Añadir 1 ${unidad}`}>
            <Plus size={14} />
          </button>
          <select
            className="select"
            style={{ width: 80 }}
            value={unidad}
            onChange={(e) => setUnidad(e.target.value as Unidad)}
          >
            {(Object.keys(UNIDADES) as Unidad[]).map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          <span className="muted" style={{ fontSize: 12 }}>= {tamano(bytes)}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {[
          { etiqueta: '500 MB', valor: 500, unidad: 'MB' as Unidad },
          { etiqueta: '1 GB', valor: 1, unidad: 'GB' as Unidad },
          { etiqueta: '10 GB', valor: 10, unidad: 'GB' as Unidad },
          { etiqueta: '50 GB', valor: 50, unidad: 'GB' as Unidad },
          { etiqueta: '100 GB', valor: 100, unidad: 'GB' as Unidad },
          { etiqueta: '1 TB', valor: 1, unidad: 'TB' as Unidad },
        ].map((atajo) => (
          <button
            key={atajo.etiqueta}
            className="btn btn--sm"
            onClick={() => {
              setValor(atajo.valor);
              setUnidad(atajo.unidad);
            }}
          >
            {atajo.etiqueta}
          </button>
        ))}
      </div>

      {objetivo && (
        <div className="muted" style={{ fontSize: 12 }}>
          Hoy ocupa <strong>{tamano(objetivo.usadoBytes)}</strong>
          {objetivo.papeleraBytes > 0 && <> ({tamano(objetivo.papeleraBytes)} en la papelera)</>}. Si fijas una cuota
          por debajo de esa cifra, la carpeta queda bloqueada para subir hasta que se libere espacio.
        </div>
      )}
    </Modal>
  );
}

const UNIDADES = { KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 } as const;
type Unidad = keyof typeof UNIDADES;

/** Elige la unidad en la que el número se lee sin decimales absurdos. */
function tamanoEditable(bytes: number): { valor: number; unidad: Unidad } {
  const unidad = (['TB', 'GB', 'MB', 'KB'] as Unidad[]).find((u) => bytes >= UNIDADES[u]) ?? 'MB';
  return { valor: +(bytes / UNIDADES[unidad]).toFixed(3), unidad };
}
